import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  ApiError,
  createAdminSeason,
  createAdminTeam,
  createTeamInvite,
  getAdminSeasons,
  getAdminTeams,
  getTeamInvites,
} from "@/lib/api";
import { invalidateScope, unscopedKey } from "@/lib/queryClient";
import type { Season } from "@/lib/types";
import { pickCurrentSeason } from "@/features/admin/components/SeasonTeamGroups";
import {
  isBootstrapTeam,
  isPendingSetupInvite,
  readSetup,
  samePendingSetupWrite,
  sameSetupRequest,
  withSetupLock,
  type PendingWrite,
} from "../onboardingState";
import type { useClubOnboarding } from "./useClubOnboarding";

type Onboarding = ReturnType<typeof useClubOnboarding>;
type Setup = Pick<Onboarding, "key" | "update"> & {
  teams: Pick<Onboarding["teams"], "refetch">;
};

async function refreshSetup(client: QueryClient) {
  await Promise.all([
    invalidateScope(),
    ...[
      "me",
      "admin-teams",
      "club-overview",
      "admin-seasons",
      "onboarding-seasons",
      "onboarding-invites",
    ].map((domain) =>
      client.invalidateQueries({ queryKey: unscopedKey(domain) }),
    ),
  ]);
}

/** #908: a first team is always created inside a season (plan §6, §7
 * "Onboarding setup"). Pick the club's current season, or create one named
 * after the current year when the club has none yet (it becomes the default
 * season). Safe on retry: a season created before an interrupted team POST
 * is found by the fresh read instead of being created twice. */
async function ensureSetupSeason(
  setup: Setup,
  client: QueryClient,
): Promise<Season> {
  const current = pickCurrentSeason(await getAdminSeasons());
  if (current) return current;
  const season = await createAdminSeason({
    name: String(new Date().getFullYear()),
    starts_on: "",
    ends_on: "",
    is_default: true,
  });
  client.setQueryData(
    unscopedKey("onboarding-seasons", { key: setup.key }),
    (previous: Season[] | undefined) => [...(previous ?? []), season],
  );
  return season;
}

async function commitSetupWrite(
  setup: Setup,
  client: QueryClient,
  intent: PendingWrite,
) {
  if (intent.kind === "team") {
    const season = await ensureSetupSeason(setup, client);
    const team = await createAdminTeam({
      ...intent.body,
      public: false,
      season_id: season.id,
    });
    client.setQueryData(
      unscopedKey("club-onboarding-teams", { key: setup.key }),
      [team],
    );
    setup.update({
      pending: null,
      teamId: team.id,
      seasonId: season.id,
      step: "season",
    });
  } else if (intent.kind === "season") {
    const season = await createAdminSeason({
      ...intent.body,
      is_default: false,
    });
    client.setQueryData(
      unscopedKey("onboarding-seasons", { key: setup.key }),
      (previous: import("@/lib/types").Season[] | undefined) => [
        ...(previous ?? []),
        season,
      ],
    );
    setup.update({ pending: null, seasonId: season.id, step: "invite" });
  } else {
    await createTeamInvite(intent.body); // Never persist tokens or API responses.
    setup.update((s) => ({
      ...s,
      pending: null,
      draft: { ...s.draft, invite: { ...s.draft.invite, email: "" } },
    }));
  }
}

/** Persist intent before POST. Uncertain writes are never automatically retried. */
export function useSetupWrite(setup: Setup) {
  const client = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: (intent: PendingWrite) =>
      withSetupLock(setup.key, async () => {
        const saved = readSetup(setup.key);
        if (saved.pending)
          throw new Error("Check the previous request before trying again.");
        if (saved.skippedWrites.some((old) => sameSetupRequest(old, intent)))
          throw new Error(
            "This request was skipped after an interrupted response. Review the records in Club admin before creating it again. For a season, use the saved season if it appears in the list.",
          );
        if (intent.kind === "team") {
          const teams = await getAdminTeams();
          if (teams.some((t) => !isBootstrapTeam(t))) {
            await setup.teams.refetch();
            throw new Error(
              "A team has already been created for this club. Open Club admin to continue.",
            );
          }
          intent = { ...intent, before: teams.map((t) => t.id) };
        } else if (intent.kind === "season") {
          intent = {
            ...intent,
            before: (await getAdminSeasons()).map((s) => s.id),
          };
        } else {
          const email = intent.body.email;
          const invites = await getTeamInvites(intent.body.team_id);
          if (
            invites.some(
              (i) => isPendingSetupInvite(i) && i.email.toLowerCase() === email,
            )
          )
            throw new Error("This person already has a pending invitation.");
          intent = { ...intent, before: invites.map((i) => i.id) };
        }
        const latest = readSetup(setup.key);
        if (
          latest.pending ||
          latest.skippedWrites.some((old) => sameSetupRequest(old, intent))
        )
          throw new Error(
            "Another setup request is in progress. Check saved records before continuing.",
          );
        setup.update({ pending: intent });
        try {
          await commitSetupWrite(setup, client, intent);
        } catch (error) {
          // A definite rejection is safe to correct. Network/5xx outcomes require read-back.
          if (
            error instanceof ApiError &&
            error.status >= 400 &&
            error.status !== 408 &&
            error.status < 500
          )
            setup.update({ pending: null });
          throw error;
        }
        await refreshSetup(client);
      }),
  });
}

function requireCurrentIntent(setup: Setup, intent: PendingWrite) {
  const current = readSetup(setup.key).pending;
  if (!current || !samePendingSetupWrite(current, intent))
    throw new Error("The setup request changed. Check saved records again.");
}

/** Fresh authorized reads distinguish a committed record, no match, and ambiguity. */
async function reconcileSetupWrite(
  setup: Setup,
  client: QueryClient,
  intent: PendingWrite,
) {
  if (intent.kind === "team") {
    const teams = await getAdminTeams();
    client.setQueryData(
      unscopedKey("club-onboarding-teams", { key: setup.key }),
      teams,
    );
    const matches = teams.filter(
      (t) =>
        !intent.before.includes(t.id) &&
        t.slug === intent.body.slug &&
        t.name === intent.body.name &&
        t.game_format === intent.body.game_format,
    );
    if (matches.length > 1)
      throw new Error(
        "More than one matching team was found. Review the records in Club admin.",
      );
    if (matches.length === 1) {
      requireCurrentIntent(setup, intent);
      setup.update({
        teamId: matches[0].id,
        seasonId: matches[0].season_id ?? null,
        step: "season",
        pending: null,
      });
      return true;
    }
  } else if (intent.kind === "season") {
    const seasons = await getAdminSeasons();
    client.setQueryData(
      unscopedKey("onboarding-seasons", { key: setup.key }),
      seasons,
    );
    const matches = seasons.filter(
      (s) =>
        !intent.before.includes(s.id) &&
        s.name === intent.body.name &&
        (s.starts_on || "") === intent.body.starts_on &&
        (s.ends_on || "") === intent.body.ends_on,
    );
    if (matches.length > 1)
      throw new Error(
        "More than one matching season was found. Review the records in Club admin.",
      );
    if (matches.length === 1) {
      requireCurrentIntent(setup, intent);
      setup.update({ seasonId: matches[0].id, step: "invite", pending: null });
      return true;
    }
  } else {
    const invites = await getTeamInvites(intent.body.team_id);
    const matches = invites.filter(
      (i) =>
        !intent.before.includes(i.id) &&
        i.email.toLowerCase() === intent.body.email &&
        i.role === intent.body.role,
    );
    if (matches.length > 1)
      throw new Error(
        "More than one matching invitation was found. Review the records in Club admin.",
      );
    if (matches.length === 1) {
      requireCurrentIntent(setup, intent);
      setup.update((s) => ({
        ...s,
        pending: null,
        draft: { ...s.draft, invite: { ...s.draft.invite, email: "" } },
      }));
      return true;
    }
  }
  return false;
}

type RecoveryInput =
  | { action: "skip" | "retry-team"; intent: PendingWrite }
  | undefined;
type RecoveryResult =
  | { status: "missing"; intent: PendingWrite }
  | { status: "resolved" | "skipped" };

export function useSetupRecovery(setup: Setup) {
  const client = useQueryClient();
  return useMutation({
    retry: false,
    onSuccess: () => refreshSetup(client),
    mutationFn: (input: RecoveryInput): Promise<RecoveryResult> =>
      withSetupLock(setup.key, async () => {
        const intent = readSetup(setup.key).pending;
        if (!intent) return { status: "resolved" };
        // The button can be stale after a different tab changes the pending request.
        if (input && !samePendingSetupWrite(input.intent, intent))
          throw new Error(
            "The setup request changed. Check saved records again.",
          );
        if (await reconcileSetupWrite(setup, client, intent))
          return { status: "resolved" };
        requireCurrentIntent(setup, intent);
        if (!input) return { status: "missing", intent };
        if (input.action === "skip") {
          if (intent.kind === "team")
            throw new Error(
              "A first team is required. Retry the original team request.",
            );
          // A read cannot prove a delayed POST will never commit. Skip without resending,
          // and retain the intent to block the same request on Back/refresh.
          setup.update((s) => ({
            ...s,
            pending: null,
            skippedWrites: [...s.skippedWrites, intent],
            step: "invite",
          }));
          return { status: "skipped" };
        }
        if (intent.kind !== "team")
          throw new Error(
            "Only the original team request can be retried safely.",
          );
        const teams = await getAdminTeams();
        requireCurrentIntent(setup, intent);
        if (teams.some((t) => !isBootstrapTeam(t)))
          throw new Error(
            "A team has already been created. Check saved records or open Club admin.",
          );
        // Repeat exactly the original slug, protected by teams.slug's UNIQUE constraint.
        // If the original POST commits meanwhile, adopt its record after the conflict.
        try {
          await commitSetupWrite(setup, client, intent);
        } catch (error) {
          if (
            error instanceof ApiError &&
            error.status === 409 &&
            (await reconcileSetupWrite(setup, client, intent))
          )
            return { status: "resolved" };
          throw error; // Keep the original pending intent, including on another interruption.
        }
        return { status: "resolved" };
      }),
  });
}
